import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apiClient, handleApiError } from '@/config/api';
import {
  CallActionResponse,
  CallAssignResponse,
  CallDetailResponse,
  CallRecordingResponse,
  UnassignedCall,
} from '@/types/api';

/**
 * Call detail, recording playback, and the admin unassigned/assign flow — the
 * mobile side of iflipweb2's CallDetailWindow / AudioPlayer / StaffAssignment.
 * Backend enforces own-or-admin (detail/recording) and admin-only (unassigned/
 * assign); this slice just proxies. Registered in BOTH store/index.ts and
 * store/index.web.ts.
 */
interface CallReviewState {
  detail: CallDetailResponse | null;
  detailLoading: boolean;
  detailError: string | null;

  recording: CallRecordingResponse | null;
  recordingLoading: boolean;

  unassigned: UnassignedCall[];
  unassignedLoading: boolean;
  unassignedError: string | null;
  unassignedAvailable: boolean;

  /** callUuid currently being assigned (drives per-row spinners), or null. */
  assigningUuid: string | null;
  /** callUuid currently being excluded, or null. */
  excludingUuid: string | null;
}

const initialState: CallReviewState = {
  detail: null,
  detailLoading: false,
  detailError: null,
  recording: null,
  recordingLoading: false,
  unassigned: [],
  unassignedLoading: false,
  unassignedError: null,
  unassignedAvailable: true,
  assigningUuid: null,
  excludingUuid: null,
};

export const getCallDetail = createAsyncThunk(
  'callReview/detail',
  async (callUuid: string, { rejectWithValue }) => {
    try {
      return await apiClient.get<CallDetailResponse>(
        `/call-performance/calls/${encodeURIComponent(callUuid)}`
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getRecording = createAsyncThunk(
  'callReview/recording',
  async (callUuid: string, { rejectWithValue }) => {
    try {
      return await apiClient.get<CallRecordingResponse>(
        `/call-performance/calls/${encodeURIComponent(callUuid)}/recording-url`
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getUnassignedCalls = createAsyncThunk(
  'callReview/unassigned',
  async (_, { rejectWithValue }) => {
    try {
      return await apiClient.get<{ available: boolean; calls: UnassignedCall[] }>(
        '/call-performance/unassigned'
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const assignCall = createAsyncThunk(
  'callReview/assign',
  async (arg: { callUuid: string; employeeId: number }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post<CallAssignResponse>(
        `/call-performance/calls/${encodeURIComponent(arg.callUuid)}/assign`,
        { employeeId: arg.employeeId }
      );
      if (!res.success) {
        return rejectWithValue(res.message || 'Assignment failed');
      }
      return { callUuid: arg.callUuid, result: res };
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const excludeCall = createAsyncThunk(
  'callReview/exclude',
  async (arg: { callUuid: string; reason: string }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post<CallActionResponse>(
        `/call-performance/calls/${encodeURIComponent(arg.callUuid)}/exclude`,
        { reason: arg.reason }
      );
      if (!res.success) {
        return rejectWithValue(res.message || 'Exclude failed');
      }
      return { callUuid: arg.callUuid };
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const callReviewSlice = createSlice({
  name: 'callReview',
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailError = null;
      state.recording = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // detail
      .addCase(getCallDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
        state.detail = null;
        state.recording = null;
      })
      .addCase(getCallDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload;
      })
      .addCase(getCallDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload as string;
      })
      // recording
      .addCase(getRecording.pending, (state) => {
        state.recordingLoading = true;
      })
      .addCase(getRecording.fulfilled, (state, action) => {
        state.recordingLoading = false;
        state.recording = action.payload;
      })
      .addCase(getRecording.rejected, (state) => {
        state.recordingLoading = false;
        state.recording = { available: false };
      })
      // unassigned
      .addCase(getUnassignedCalls.pending, (state) => {
        state.unassignedLoading = true;
        state.unassignedError = null;
      })
      .addCase(getUnassignedCalls.fulfilled, (state, action) => {
        state.unassignedLoading = false;
        state.unassignedAvailable = action.payload.available;
        state.unassigned = action.payload.calls ?? [];
      })
      .addCase(getUnassignedCalls.rejected, (state, action) => {
        state.unassignedLoading = false;
        state.unassignedError = action.payload as string;
      })
      // assign
      .addCase(assignCall.pending, (state, action) => {
        state.assigningUuid = action.meta.arg.callUuid;
      })
      .addCase(assignCall.fulfilled, (state, action) => {
        state.assigningUuid = null;
        // Optimistically drop the freshly-assigned call from the unassigned list.
        state.unassigned = state.unassigned.filter((c) => c.callUuid !== action.payload.callUuid);
      })
      .addCase(assignCall.rejected, (state) => {
        state.assigningUuid = null;
      })
      // exclude
      .addCase(excludeCall.pending, (state, action) => {
        state.excludingUuid = action.meta.arg.callUuid;
      })
      .addCase(excludeCall.fulfilled, (state, action) => {
        state.excludingUuid = null;
        state.unassigned = state.unassigned.filter((c) => c.callUuid !== action.payload.callUuid);
      })
      .addCase(excludeCall.rejected, (state) => {
        state.excludingUuid = null;
      });
  },
});

export const { clearDetail } = callReviewSlice.actions;
export default callReviewSlice.reducer;
